/**
 * GovernancePulse — hero section for the Governance tab (RAISE-16886).
 *
 * Renders a radial dial (composite score 0–100), 4 KPI tiles (2×2 grid),
 * and an attention list (max 3 items). Each KPI independently degrades
 * with a "Plan required" chip on 403. Loading renders 3 skeleton blocks.
 */

import { useTranslation } from "react-i18next";
import {
  RadialBarChart,
  RadialBar,
  ResponsiveContainer,
  PolarAngleAxis,
} from "recharts";
import { useGovernancePulse } from "@/hooks/useGovernancePulse";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import type { PulseKpi } from "@/hooks/useGovernancePulse";

// ---------------------------------------------------------------------------
// KPI tile
// ---------------------------------------------------------------------------

interface KpiTileProps {
  kpi: PulseKpi;
}

function KpiTile({ kpi }: KpiTileProps) {
  const { t } = useTranslation();

  const labelKey = `projectPortal.governance.pulse.kpi.${kpi.id}`;
  const label = t(labelKey, { defaultValue: kpi.id });

  if (kpi.planGated) {
    return (
      <div className="rounded-lg border p-3 space-y-1">
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        <Badge variant="outline" className="text-xs">
          {t("projectPortal.governance.pulse.planRequired", {
            defaultValue: "Plan required",
          })}
        </Badge>
      </div>
    );
  }

  const displayValue =
    kpi.value === null
      ? "—"
      : kpi.id === "gates" || kpi.id === "coverage"
        ? `${Math.round(kpi.value)}%`
        : String(kpi.value);

  return (
    <div className="rounded-lg border p-3 space-y-1">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="text-xl font-bold">{displayValue}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Radial dial
// ---------------------------------------------------------------------------

interface RadialDialProps {
  score: number;
}

function RadialDial({ score }: RadialDialProps) {
  const data = [{ value: score, fill: "hsl(var(--primary))" }];

  return (
    <div className="relative w-32 h-32 mx-auto">
      <ResponsiveContainer width="100%" height="100%">
        <RadialBarChart
          data={data}
          startAngle={220}
          endAngle={-40}
          innerRadius="70%"
          outerRadius="100%"
          barSize={10}
        >
          <PolarAngleAxis
            type="number"
            domain={[0, 100]}
            tick={false}
          />
          <RadialBar
            dataKey="value"
            cornerRadius={4}
            background={{ fill: "hsl(var(--muted))" }}
          />
        </RadialBarChart>
      </ResponsiveContainer>
      <span className="absolute inset-0 flex items-center justify-center text-2xl font-bold">
        {Math.round(score)}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface GovernancePulseProps {
  projectSlug: string;
}

export function GovernancePulse({ projectSlug }: GovernancePulseProps) {
  const { t } = useTranslation();
  const { score, kpis, attention, loading } = useGovernancePulse(projectSlug);

  if (loading) {
    return (
      <div className="space-y-4" data-testid="governance-pulse-loading">
        <Skeleton className="h-32 w-32 rounded-full mx-auto" />
        <div className="grid grid-cols-2 gap-3">
          <Skeleton className="h-16 rounded-lg" />
          <Skeleton className="h-16 rounded-lg" />
          <Skeleton className="h-16 rounded-lg" />
          <Skeleton className="h-16 rounded-lg" />
        </div>
        <Skeleton className="h-8 rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="governance-pulse">
      {/* Radial dial */}
      {score === null ? (
        <div className="flex items-center justify-center h-32">
          <Badge variant="outline">
            {t("projectPortal.governance.pulse.dataUnavailable", {
              defaultValue: "Data unavailable",
            })}
          </Badge>
        </div>
      ) : (
        <RadialDial score={score} />
      )}

      {/* KPI 2×2 grid */}
      <div className="grid grid-cols-2 gap-3">
        {kpis.map((kpi) => (
          <KpiTile key={kpi.id} kpi={kpi} />
        ))}
      </div>

      {/* Attention list (max 3) */}
      {attention.length > 0 && (
        <ul className="space-y-1" data-testid="attention-list">
          {attention.map((item) => (
            <li
              key={item.key}
              className={`text-xs flex items-start gap-1 ${
                item.severity === "warn"
                  ? "text-destructive"
                  : "text-muted-foreground"
              }`}
            >
              <span>{item.severity === "warn" ? "⚠" : "ℹ"}</span>
              <span>
                {t(item.message, { defaultValue: item.key })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
